/**
 * §7.4 開工單與領料。核可區塊需 `issue.approve`。
 *
 * 這一頁是 §7.5 的示範重點：**判定留在前端做即時回饋**
 * （挑片時切換 by_id／by_weight，判定框要即時變），但**以後端為準**
 * ——過帳時後端用同一個 `judgePick` 重算，不一致就回 `409 JUDGEMENT_MISMATCH`。
 *
 * §10.1 設備並行互斥：設備下拉選單中已有工單的那台顯示為 disabled 並標明工單號。
 */

import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client.js'
import { useMutation, useQuery } from '../api/hooks.js'
import { useAuth } from '../auth/AuthContext.js'
import { emptyPick, PanelPicker, toDerivedItem, usePickJudgement, type PickState } from '../components/PanelPicker.js'
import { Alert, Button, Card, Empty, Field, KV, Mono, Pill, Select, Steps, Table, Td, TextInput } from '../components/ui.js'
import {
  emptyWeighValue, toWeighPayload, WeighEntry, weighComplete, weighNetKg, useWeighDraft,
  type PanelMeta, type WeighValue,
} from '../components/WeighEntry.js'
import { useConfig, useDict } from '../config/ConfigContext.js'
import { count, kgUnit, modelOptionLabel } from '../../../shared/lib/format.js'
import { Link, navigate } from '../router.js'

interface WorkOrderRow {
  work_order_id: string; station_id: string; resource_id: string; status: string
}

export function IssueOrderScreen() {
  const [workOrderId, setWorkOrderId] = useState<string | null>(null)
  const open = useQuery<{ items: WorkOrderRow[] }>('/api/work-orders?status=open&limit=100')

  return (
    <div className="space-y-4">
      <Steps steps={['開工單', '挑料與判定', '出庫過帳']} current={workOrderId ? 1 : 0} />
      {workOrderId
        ? <IssueToWorkOrder workOrderId={workOrderId} onDone={() => { setWorkOrderId(null); open.reload() }} />
        : <OpenWorkOrder openOrders={open.data?.items ?? []} onOpened={setWorkOrderId} onReload={open.reload} />}
    </div>
  )
}

// ── Step 1：開工單 ─────────────────────────────────────────────────────────

function OpenWorkOrder({ openOrders, onOpened, onReload }: {
  openOrders: WorkOrderRow[]
  onOpened: (id: string) => void
  onReload: () => void
}) {
  const { config } = useConfig()
  const dict = useDict()
  const [stationId, setStationId] = useState('')
  const [resourceId, setResourceId] = useState('')
  const [plannedCount, setPlannedCount] = useState('')
  const [modelCode, setModelCode] = useState('')
  const [purged, setPurged] = useState(true)

  const stations = (config?.stations ?? []).filter((s) => s.enabled)
  const resources = (config?.resources ?? []).filter((r) => r.enabled && r.stationId === stationId)
  // §10.1 已有進行中工單的設備：選單裡 disable 並標明工單號
  const busy = useMemo(
    () => new Map(openOrders.map((w) => [w.resource_id, w.work_order_id])),
    [openOrders],
  )
  const batchRule = config?.batchRules?.find((b) => b.stationId === stationId)

  useEffect(() => {
    if (!stationId && stations[0]) setStationId(stations[0].stationId)
  }, [stations, stationId])
  useEffect(() => {
    const free = resources.find((r) => !busy.has(r.resourceId))
    setResourceId(free?.resourceId ?? resources[0]?.resourceId ?? '')
  }, [stationId, resources.length])

  const post = useMutation<void, { workOrderId: string }>(async (_a, key) =>
    api.post('/api/work-orders', {
      stationId, resourceId,
      plannedCount: plannedCount ? Number(plannedCount) : 0,
      modelCode: modelCode || null,
      purged,
    }, { idempotencyKey: key }))

  return (
    <>
      {openOrders.length > 0 && (
        <Card title="進行中的工單" subtitle="可以直接接續領料">
          <Table head={['工單', '站台', '設備', '']} dense>
            {openOrders.map((w) => (
              <tr key={w.work_order_id}>
                <Td><Mono>{w.work_order_id}</Mono></Td>
                <Td>{dict.station(w.station_id)}</Td>
                <Td className="text-xs">{dict.resource(w.resource_id)}</Td>
                <Td>
                  <Button size="sm" variant="primary" onClick={() => onOpened(w.work_order_id)}>領料</Button>
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      <Card title="Step 1 — 開工單">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="站台" required>
            <Select value={stationId} onChange={(e) => setStationId(e.target.value)}>
              {stations.map((s) => <option key={s.stationId} value={s.stationId}>{s.label}</option>)}
            </Select>
          </Field>
          <Field label="設備" hint="§10.1 一台設備同時只能有一張工單" required>
            <Select value={resourceId} onChange={(e) => setResourceId(e.target.value)}>
              {resources.map((r) => {
                const wo = busy.get(r.resourceId)
                return (
                  <option key={r.resourceId} value={r.resourceId} disabled={Boolean(wo)}>
                    {r.label}{wo ? `（有進行中工單 ${wo}）` : ''}
                  </option>
                )
              })}
            </Select>
          </Field>
          <Field label="預計片數" hint={batchRule?.maxCount
            ? `上限 ${batchRule.maxCount}（${batchRule.source}）` : undefined}>
            <TextInput inputMode="numeric" value={plannedCount}
              onChange={(e) => setPlannedCount(e.target.value.replace(/\D/g, ''))} />
          </Field>
          <Field label="型號">
            <Select value={modelCode} onChange={(e) => setModelCode(e.target.value)}>
              <option value="">未指定</option>
              {(config?.modelSpecs ?? []).map((m) => (
                <option key={m.modelCode} value={m.modelCode}>
                  {modelOptionLabel(m)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <label className="mt-3 flex items-start gap-2 text-sm text-ink-700">
          <input type="checkbox" checked={purged} className="mt-1"
            onChange={(e) => setPurged(e.target.checked)} />
          <span>
            已清機
            <span className="block text-xs text-ink-500">
              沒清機的話，繳回可能大於領出（前一批的殘留被帶出來）。
              那種「負損耗」在結算時是預期行為，不會被當成異常。
            </span>
          </span>
        </label>

        {post.error && (
          <div className="mt-3">
            {/* §10.1 被擋下時的訊息帶著佔用的工單號 */}
            <Alert tone="danger" title="開工單失敗">
              {post.error.message}
              <div className="mt-2">
                <Button size="sm" onClick={onReload}>重新整理設備狀態</Button>
              </div>
            </Alert>
          </div>
        )}

        <div className="mt-4">
          <Button variant="primary" size="lg"
            disabled={!stationId || !resourceId || busy.has(resourceId) || post.pending}
            onClick={async () => { const r = await post.run(); if (r) onOpened(r.workOrderId) }}>
            {post.pending ? '開立中…' : '開工單'}
          </Button>
        </div>
      </Card>
    </>
  )
}

// ── Step 2、3：挑料 → 出庫過帳 ─────────────────────────────────────────────

function IssueToWorkOrder({ workOrderId, onDone }: { workOrderId: string; onDone: () => void }) {
  const { config } = useConfig()
  const dict = useDict()
  const { can } = useAuth()
  const wo = useQuery<{ workOrder: WorkOrderRow }>(`/api/work-orders/${encodeURIComponent(workOrderId)}`)
  const stock = useQuery<{ items: Record<string, unknown>[] }>('/api/inventory/items?limit=200')

  const [itemId, setItemId] = useState('')
  const [wantCount, setWantCount] = useState('')
  const [pick, setPick] = useState<PickState>(emptyPick())
  const [weigh, setWeigh] = useState<WeighValue>(() => emptyWeighValue())
  const [approvedBy, setApprovedBy] = useState('')

  const detail = useQuery<{ item: Record<string, unknown>; panels: PanelMeta[] }>(
    itemId ? `/api/inventory/items/${encodeURIComponent(itemId)}` : null,
  )

  const station = wo.data?.workOrder.station_id ?? null
  const item = detail.data ? toDerivedItem(detail.data.item, detail.data.panels.map((p) => p.panel_id)) : null
  const panels = detail.data?.panels ?? []

  // 本站的「出庫」量測點（kind = 'issue'）
  const mp = useMemo(() => {
    if (!config || !station || !item) return null
    return config.measurementPoints.find(
      (m) => m.stationId === station && m.kind === 'issue' && m.materialCode === item.materialCode,
    ) ?? null
  }, [config, station, item])

  const want = wantCount ? Number(wantCount) : (item?.panelIds.length ?? 0)
  // §7.5 前端即時判定——切換 by_id／by_weight 這裡就會變
  const judgement = usePickJudgement(
    item ?? toDerivedItem({}, []), panels, config, pick, want,
  )

  const takenPanels = useMemo(() => {
    if (!item) return []
    const isPartial = want > 0 && want < item.panelIds.length
    return isPartial && pick.splitBasis === 'by_id' ? pick.picked : item.panelIds
  }, [item, want, pick])

  const draft = useWeighDraft(itemId && mp ? `issue.${workOrderId}.${itemId}` : '', weigh, setWeigh)

  const post = useMutation<void, { issueOrderId: string; issuedKg: number | null; panelCount: number }>(
    async (_a, key) => api.post('/api/issues', {
      workOrderId,
      itemId,
      measurementPointId: mp!.measurementPointId,
      splitBasis: want > 0 && item && want < item.panelIds.length ? pick.splitBasis : null,
      pickedPanelIds: pick.picked.length > 0 ? pick.picked : undefined,
      byWeightKg: pick.splitBasis === 'by_weight' && pick.byWeightKg ? Number(pick.byWeightKg) : undefined,
      approvedBy: approvedBy || undefined,
      weigh: toWeighPayload(mp!, weigh, takenPanels),
      // §7.5 把前端的判定一起送上去比對
      clientJudgement: judgement ? {
        resolutionGrade: judgement.resolutionGrade,
        homogeneityGrade: judgement.homogeneityGrade,
        needsApproval: judgement.needsApproval,
      } : undefined,
    }, { idempotencyKey: key }),
  )

  const inStock = (stock.data?.items ?? []).filter((x) => x.status === 'in_stock')
  const complete = mp ? weighComplete(mp, weigh, takenPanels) : false
  const isPartial = Boolean(item && want > 0 && want < item.panelIds.length)
  const pickOk = !isPartial || pick.splitBasis === 'by_weight' || pick.picked.length === want
  const approvalOk = !judgement?.needsApproval || (approvedBy !== '' && can('issue.approve'))

  return (
    <>
      <Card
        title={<>工單 <Mono>{workOrderId}</Mono></>}
        subtitle={wo.data ? `${dict.station(station)} · ${dict.resource(wo.data.workOrder.resource_id)}` : undefined}
        right={<Button size="sm" onClick={onDone}>換一張工單</Button>}
      />

      <Card title="Step 2 — 挑料">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="來源料件" required>
            <Select value={itemId} onChange={(e) => { setItemId(e.target.value); setPick(emptyPick()) }}>
              <option value="">請選擇</option>
              {inStock.map((x) => (
                <option key={x.item_id as string} value={x.item_id as string}>
                  {x.item_id as string} · {dict.material(x.material_code as string)} ·
                  {' '}{count(x.panel_count as number)} 片
                  {x.net_kg === null ? '（無重量帳）' : ` · ${kgUnit(Number(x.net_kg), 1)}`}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="要領幾片" hint={item ? `這個料件共 ${count(item.panelIds.length)} 片。留空 = 整件領出` : undefined}>
            <TextInput inputMode="numeric" value={wantCount}
              onChange={(e) => setWantCount(e.target.value.replace(/\D/g, ''))} />
          </Field>
        </div>

        {inStock.length === 0 && !stock.loading && (
          <div className="mt-3">
            <Empty>目前沒有在庫料件可以領。先到<Link to="/receiving" className="mx-1 underline">進庫</Link>建檔。</Empty>
          </div>
        )}

        {item && config && (
          <div className="mt-4">
            <PanelPicker
              item={item} panels={panels} config={config} wantCount={want}
              value={pick} onChange={setPick} judgement={judgement}
            />
          </div>
        )}
      </Card>

      {item && mp && config && (
        <Card
          title="Step 3 — 出庫過帳"
          subtitle={`量測點 ${mp.measurementPointId} · ${
            mp.weightSource === 'booked' ? '帳面繼承' : '實秤'} · ${
            mp.granularity === 'panel' ? '逐片秤' : '整批秤'}`}
        >
          <div className="mb-3 rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-sm">
            料件 <Mono>{item.itemId}</Mono> · {dict.material(item.materialCode)} ·
            領出 <strong>{count(takenPanels.length)}</strong> 片
            {isPartial && <> · 分裂方式 <Mono>{pick.splitBasis}</Mono></>}
          </div>

          <WeighEntry
            mp={mp} config={config} materialCode={item.materialCode}
            panels={panels.filter((p) => takenPanels.includes(p.panel_id))}
            value={weigh} onChange={setWeigh}
          />

          {/* §6.1 issue.approve：核可區塊需要這個權限 */}
          {judgement?.needsApproval && (
            <div className="mt-4">
              <Alert tone="warn" title="這次領料需要個案核可">
                {judgement.resolutionReason}。{judgement.splitNote}。
                {can('issue.approve') ? (
                  <div className="mt-2 max-w-[240px]">
                    <Field label="核可人" required>
                      <TextInput value={approvedBy} onChange={(e) => setApprovedBy(e.target.value)}
                        placeholder="主管代號" />
                    </Field>
                  </div>
                ) : (
                  <p className="mt-2">
                    您的帳號沒有核可權限（<Mono>issue.approve</Mono>）。
                    請改用「按 PV 號挑選」，或請主管來核可。
                  </p>
                )}
              </Alert>
            </div>
          )}

          {post.error && (
            <div className="mt-3">
              <Alert tone="danger" title="過帳失敗">{post.error.message}</Alert>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              variant="primary" size="lg"
              disabled={!complete || !pickOk || !approvalOk || post.pending}
              onClick={async () => {
                const r = await post.run()
                if (r) {
                  draft.clear()
                  navigate(`/station/${station}`)
                }
              }}
            >{post.pending ? '過帳中…' : '出庫過帳'}</Button>

            {mp.weightSource !== 'booked' && (
              <span className="text-sm text-ink-600">
                淨重 <strong className="tabular-nums">{kgUnit(weighNetKg(mp, weigh, takenPanels), mp.decimals)}</strong>
              </span>
            )}
            {!pickOk && <Pill tone="amber">挑選片數與要領的數量不符</Pill>}
            {!complete && <Pill tone="amber">重量還沒輸入完</Pill>}
            {!approvalOk && <Pill tone="amber">需要核可人</Pill>}
          </div>
        </Card>
      )}

      {item && !mp && (
        <Card title="Step 3 — 出庫過帳" tone="warn">
          <Alert tone="warn" title="找不到對應的量測點">
            本站（{dict.station(station)}）沒有針對 {dict.material(item.materialCode)} 的出庫量測點。
            請主管到作業設定確認量測點的站台與物料設定。
          </Alert>
        </Card>
      )}

      {item && (
        <KV items={[
          ['解析度', judgement?.resolutionGrade ?? '—'],
          ['同質性', judgement?.homogeneityGrade ?? '—'],
          ['需核可', judgement?.needsApproval ? '是' : '否'],
        ]} />
      )}
    </>
  )
}
